import type { Easing, Transition } from 'motion/react';

import type { ChangeLevel, CubicBezier, MilestoneTier } from './tokens.ts';
import {
  CHANGE_LEVELS,
  CHANGE_LEVELS_POINTS,
  CHANGE_LEVELS_RELATIVE,
  DUR_CURVE,
  EASE_GAIN,
  EASE_GRAPH,
  EASE_LOSS,
  EASE_SWITCH,
  FIRST_LOAD_TRAVEL,
  MILESTONE_TIERS,
} from './tokens.ts';

type Direction = 'up' | 'down' | 'neutral';

/** `currency` grades by dollars moved, `percent` by points moved, `relative` by proportion of the figure. */
type ChangeKind = 'currency' | 'percent' | 'relative';

const STILL_LEVEL: ChangeLevel = { id: 'still', upTo: 0, amp: 0, coins: 0 };

function lastLevel(levels: readonly ChangeLevel[]): ChangeLevel {
  return levels[levels.length - 1] ?? STILL_LEVEL;
}

function levelFor(levels: readonly ChangeLevel[], moved: number): ChangeLevel {
  return levels.find((level) => moved < level.upTo) ?? lastLevel(levels);
}

/** How loudly a figure reacts to moving from `from` to `to`. Percent values are in points (1.0 is one percent). */
function changeLevel(from: number, to: number, kind: ChangeKind = 'currency'): ChangeLevel {
  if (!Number.isFinite(from) || !Number.isFinite(to)) return CHANGE_LEVELS[0] ?? STILL_LEVEL;
  if (kind === 'percent') return levelFor(CHANGE_LEVELS_POINTS, Math.abs(to - from));
  if (kind === 'relative')
    return levelFor(CHANGE_LEVELS_RELATIVE, Math.abs(to - from) / Math.max(Math.abs(from), 1e-9));
  return levelFor(CHANGE_LEVELS, Math.abs(to - from));
}

/** The biggest round number a count-up passes on its way from `from` to `to`. Sitting near one earns nothing. */
function milestoneCrossing(from: number, to: number): MilestoneTier | null {
  if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) return null;
  function crosses(step: number): boolean {
    return Math.floor(to / step) > Math.floor(from / step);
  }
  if (crosses(MILESTONE_TIERS.tenMillion.step)) return MILESTONE_TIERS.tenMillion;
  if (crosses(MILESTONE_TIERS.fiveMillion.step)) return MILESTONE_TIERS.fiveMillion;
  if (crosses(MILESTONE_TIERS.million.step)) return MILESTONE_TIERS.million;
  if (crosses(MILESTONE_TIERS.halfMillion.step)) return MILESTONE_TIERS.halfMillion;
  return null;
}

const MILESTONES = [1e9, 5e8, 1e8, 5e7, 1e7, 5e6, 1e6, 5e5, 1e5, 5e4, 1e4];

/** A figure sitting just above a round number still starts its roll below the line, so you watch it tick over. */
function milestoneJustPassed(value: number, band = 0.04): number | null {
  const magnitude = Math.abs(value);
  return MILESTONES.find((m) => magnitude >= m && magnitude <= m * (1 + band)) ?? null;
}

/** Shorten the distance without moving the destination: the figure still lands on `value`, it just starts nearer. */
function clampTravel(value: number, from: number, scale = 1): number {
  if (!Number.isFinite(value) || !Number.isFinite(from)) return from;
  const falling = from > value;
  const base = Math.max(Math.abs(value), Math.abs(from), 1e-9);
  const cap = base * (falling ? FIRST_LOAD_TRAVEL.loss : FIRST_LOAD_TRAVEL.gain) * scale;
  if (Math.abs(value - from) <= cap) return from;
  return value + (falling ? cap : -cap);
}

function autoRollUnit(magnitude: number): number {
  if (magnitude >= 1000) return Math.max(100, Math.round((magnitude * 0.02) / 10) * 10);
  if (magnitude >= 1) return 1;
  return 0.1;
}

function rollStartPlain(value: number, unitHint?: number): number {
  const v = Number.isFinite(value) ? value : 0;
  const a = Math.abs(v);
  if (a === 0) return 0;
  // Travel scales with magnitude, ~2% of the figure floored at one unit of the place value below it,
  // so a 600ms roll has something to traverse at any size.
  const auto = autoRollUnit(a);
  const unit = unitHint ?? auto;
  const sign = v < 0 ? -1 : 1;
  return v - sign * unit;
}

/** Where a roll begins: one unit below the value, or just under a milestone the figure has just cleared. */
function rollStart(value: number, unitHint?: number): number {
  const milestone = unitHint === undefined ? milestoneJustPassed(value) : null;
  if (milestone !== null) {
    const sign = value < 0 ? -1 : 1;
    return sign * (milestone - milestone * 0.012);
  }
  return rollStartPlain(value, unitHint);
}

function bezier(curve: CubicBezier): [number, number, number, number] {
  return [curve[0], curve[1], curve[2], curve[3]];
}

function cssEase(curve: CubicBezier): string {
  return `cubic-bezier(${curve.join(', ')})`;
}

/** Gains accelerate into the final value, losses ease off into it; neutral is the symmetric switch curve. */
function easingFor(direction: Direction): Easing {
  if (direction === 'up') return bezier(EASE_GAIN);
  if (direction === 'down') return bezier(EASE_LOSS);
  return EASE_SWITCH;
}

/** The gain waits for the headline to land, and the headline takes longer the further it has to count. */
function gainDelayFor(from: number, to: number, base = 0.42): number {
  if (!Number.isFinite(from) || !Number.isFinite(to)) return base;
  const span = Math.min(1, Math.abs(to - from) / Math.max(Math.abs(to), 1e-9));
  return base + Math.min(0.9, span * 1.6);
}

function graphTransition(duration = DUR_CURVE): Transition {
  return { duration, ease: bezier(EASE_GRAPH) };
}

function seriesDirection(values: readonly number[]): Direction {
  if (values.length < 2) return 'neutral';
  return (values[values.length - 1] ?? 0) >= (values[0] ?? 0) ? 'up' : 'down';
}

/**
 * The span of glyphs that moves: first digit to last, interior punctuation included, a sign directly in
 * front of the first digit included. The frame (a leading `$` or `(`, a trailing `%` or `)`) stays put.
 */
function movingRange(chars: readonly string[]): readonly [number, number] {
  let first = -1;
  let last = -1;
  chars.forEach((c, i) => {
    if (/\d/.test(c)) {
      if (first < 0) first = i;
      last = i;
    }
  });
  while (first > 0 && /[+\-−]/.test(chars[first - 1] ?? '')) first -= 1;
  return [first, last];
}

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export {
  bezier,
  changeLevel,
  cssEase,
  clampTravel,
  easingFor,
  gainDelayFor,
  graphTransition,
  milestoneCrossing,
  milestoneJustPassed,
  movingRange,
  prefersReducedMotion,
  rollStart,
  seriesDirection,
};
export type { ChangeKind, Direction };
